import { Graphics, type Sprite } from 'pixi.js';
import type { DechaineEffect } from '../../data/full/avatar';
import { clamp01, lerp, smoothstep } from '../../shared/scalar';
import { AIR, FIRE, WATER } from '../../sim/full/attacks/cycle';
import { UNLEASHED, unleashedReach, unleashedTicks } from '../../sim/full/skills/dechaine';
import type { PropState, SimState } from '../../sim/state';
import { statValue } from '../../sim/stats';
import type { Family, RenderContext } from '../context';
import type { Frame } from '../frame';
import { type Sheet, TAU, paintSheet, paintedLater } from '../paint';
import { add, byId, hide, setTint } from '../util';
import { ViewPool } from '../views';
import { ELEMENT_COLORS } from './avatar-paint';
import { RING_FLAME, RING_FLAME_PIECES } from './avatar-giants-paint';
import { Silk, type SilkColors } from './avatar-silk';

const [FIRE_COLOR, WATER_COLOR, AIR_COLOR] = ELEMENT_COLORS;
const FIRE_LIGHT = 0xffd46e;
const WATER_DEEP = 0x1d5fb4;
const FOAM = 0xd2efff;
const OUT = 0x040612;
const DENSITY = 2;

const RING = {
  flames: 30,
  size: 1.3,
  flicker: 3,
  turn: 0.25,
  calmTurn: 0.08,
  fill: 0.14,
  line: 2.2,
  glow: 1.6,
  fadeBeats: 1,
} as const;
const WAVE = {
  depth: 60,
  step: 8,
  ripple: 3,
  strips: [0.85, 0.75, 0.45, 0.15],
  foam: [12, 24, 38],
  crest: 3.4,
  crestOutline: 6,
  curlEvery: 64,
  curl: { radius: 9, turns: 1.4, steps: 18, width: 2.4, outline: 4.4 },
  fadeShare: 0.15,
} as const;
const TORNADO = {
  arms: 3,
  steps: 26,
  turns: 3.6,
  width: 0.3,
  spin: 0.3,
  calmSpin: 0.08,
  fill: 0.12,
  glow: 1.8,
  fadeIn: 0.3,
  fadeOut: 0.6,
} as const;
const TORNADO_SILK: SilkColors = {
  band: 0x9cc4ee,
  bandLine: 0xffffff,
  light: 0xffffff,
  lightLine: 0xffffff,
};
const RIFT = {
  step: 13,
  scale: 1.5,
  lips: [
    [26, 0x5a3c1e, 0.35],
    [18, 0x3c2612, 0.6],
  ],
  crack: 11,
  core: [
    [4, 0xff8c28, 0.7],
    [1, 0xffd890, 1],
  ],
  spike: 20,
  spikeHalf: 6,
  spikeLean: 3,
  spikeRise: 30,
  fadeBeats: 1,
} as const;
const JAG = [0, 4, -3, 5, -5, 2, -4, 5, -2, 4, -5, 3, -3, 5, -4, 2, -5, 4, -2, 5, -3, 4, -5, 3, -4];

interface FormView {
  readonly glow: Sprite;
  readonly flames: readonly Sprite[];
}

interface Moment {
  element: number;
  reach: number;
  size: number;
  beats: number;
  totalBeats: number;
  share: number;
  x: number;
  y: number;
}

export function createUnleashed(ctx: RenderContext): Family {
  const { layers, textures } = ctx;
  const unleashes = new Map<string, DechaineEffect>();
  for (const [classId, effect] of ctx.skillEffects) {
    if (effect.kind === 'dechaine') {
      unleashes.set(classId, effect);
    }
  }
  const sheet = paintedLater((): Sheet<keyof typeof RING_FLAME_PIECES> =>
    paintSheet(RING_FLAME_PIECES, DENSITY),
  );
  const floor = new Graphics();
  layers.zones.addChild(floor);
  const arms = Array.from({ length: TORNADO.arms }, () => new Silk(TORNADO.steps + 1));
  const moment: Moment = {
    element: FIRE,
    reach: 0,
    size: 1,
    beats: 0,
    totalBeats: 1,
    share: 0,
    x: 0,
    y: 0,
  };
  let views: ViewPool<FormView> | undefined;

  function viewPool(): ViewPool<FormView> {
    const { shapes } = sheet.get();
    views ??= new ViewPool<FormView>(
      () => ({
        glow: add(layers.fx, textures.halo),
        flames: Array.from({ length: RING.flames }, () => add(layers.weapons, shapes.ringFlame, 0)),
      }),
      (view) => {
        hide(view.glow, ...view.flames);
      },
    );
    return views;
  }

  function drawRing(loose: PropState, effect: DechaineEffect, view: FormView, frame: Frame): void {
    const { reach, beats, totalBeats, size } = moment;
    const fade = 1 - clamp01((beats - (totalBeats - RING.fadeBeats)) / RING.fadeBeats);
    if (fade <= 0 || reach <= 0) {
      return;
    }
    const { x, y } = loose;
    const grown = reach / (effect.ring.radius * size);
    floor.circle(x, y, reach).fill({ color: FIRE_COLOR, alpha: RING.fill * fade });
    floor.circle(x, y, reach).stroke({ width: RING.line, color: FIRE_LIGHT, alpha: 0.8 * fade });
    view.glow.visible = true;
    view.glow.position.set(x, y);
    view.glow.scale.set((reach * RING.glow) / textures.halo.radius);
    view.glow.alpha = 0.45 * fade * frame.light.haloAlpha;
    setTint(view.glow, FIRE_COLOR);
    const time = frame.now / frame.tempo.ticksPerBeat;
    const turn = beats * (frame.calm ? RING.calmTurn : RING.turn);
    for (let index = 0; index < view.flames.length; index += 1) {
      const flame = view.flames[index];
      if (flame === undefined) {
        continue;
      }
      const angle = (index * TAU) / RING.flames + turn;
      const height =
        RING_FLAME + (frame.calm ? 0 : RING.flicker * Math.sin(index * 1.7 + time * 3.2));
      flame.visible = true;
      flame.position.set(x + Math.cos(angle) * reach, y + Math.sin(angle) * reach);
      flame.rotation = angle;
      flame.scale.set((RING.size * grown * height) / RING_FLAME, RING.size * grown);
      flame.alpha = fade;
    }
  }

  function crestAt(across: number, ripple: number): number {
    return moment.reach + ripple * Math.sin(across * 0.07 + moment.beats * 3);
  }

  function waveLine(loose: PropState, half: number, ripple: number, behind: number, sway: number) {
    const { beats } = moment;
    for (let across = -half; across <= half; across += WAVE.step) {
      const along =
        crestAt(across, ripple) - behind - sway * Math.sin(across * 0.09 - beats * 2 + behind);
      const px = loose.x + loose.vx * along - loose.vy * across;
      const py = loose.y + loose.vy * along + loose.vx * across;
      if (across === -half) {
        floor.moveTo(px, py);
      } else {
        floor.lineTo(px, py);
      }
    }
  }

  function drawWave(loose: PropState, effect: DechaineEffect, frame: Frame): void {
    const { reach, share, size } = moment;
    const fade = 1 - clamp01((share - (1 - WAVE.fadeShare)) / WAVE.fadeShare);
    if (fade <= 0) {
      return;
    }
    const half = (effect.wave.width * size) / 2;
    const ax = loose.vx;
    const ay = loose.vy;
    const ripple = frame.calm ? 0 : WAVE.ripple;
    const depth = Math.min(reach, WAVE.depth);
    for (let strip = 0; strip < WAVE.strips.length; strip += 1) {
      const front = (strip * depth) / WAVE.strips.length;
      const back = ((strip + 1) * depth) / WAVE.strips.length;
      waveLine(loose, half, ripple, front, 0);
      for (let across = half; across >= -half; across -= WAVE.step) {
        const along = crestAt(across, ripple) - back;
        floor.lineTo(loose.x + ax * along - ay * across, loose.y + ay * along + ax * across);
      }
      floor.closePath().fill({
        color: strip === 0 ? WATER_COLOR : WATER_DEEP,
        alpha: (WAVE.strips[strip] ?? 0) * fade,
      });
    }
    for (const behind of WAVE.foam) {
      if (behind > depth) {
        break;
      }
      waveLine(loose, half, ripple, behind, frame.calm ? 0 : 2);
      floor.stroke({ width: 0.8, color: FOAM, alpha: 0.45 * fade, join: 'round' });
    }
    waveLine(loose, half, ripple, 0, 0);
    floor.stroke({
      width: WAVE.crestOutline,
      color: OUT,
      alpha: 0.9 * fade,
      cap: 'round',
      join: 'round',
    });
    waveLine(loose, half, ripple, 0, 0);
    floor.stroke({ width: WAVE.crest, color: 0xffffff, alpha: fade, cap: 'round', join: 'round' });
    for (let across = -half + WAVE.curlEvery / 2; across < half; across += WAVE.curlEvery) {
      const along = crestAt(across, ripple);
      foamCurl(loose, across, along);
      floor.stroke({
        width: WAVE.curl.outline,
        color: OUT,
        alpha: 0.9 * fade,
        cap: 'round',
        join: 'round',
      });
      foamCurl(loose, across, along);
      floor.stroke({
        width: WAVE.curl.width,
        color: 0xffffff,
        alpha: fade,
        cap: 'round',
        join: 'round',
      });
    }
  }

  function foamCurl(loose: PropState, across: number, along: number): void {
    const { curl } = WAVE;
    for (let step = 0; step <= curl.steps; step += 1) {
      const turn = step / curl.steps;
      const angle = Math.PI / 2 - turn * curl.turns * TAU;
      const r = curl.radius * (1 - 0.75 * turn);
      const sideways = across + 4 + Math.cos(angle) * r;
      const forward = along + 4 - Math.sin(angle) * r;
      const px = loose.x + loose.vx * forward - loose.vy * sideways;
      const py = loose.y + loose.vy * forward + loose.vx * sideways;
      if (step === 0) {
        floor.moveTo(px, py);
      } else {
        floor.lineTo(px, py);
      }
    }
  }

  function drawTornado(view: FormView, frame: Frame): void {
    const { reach, beats, totalBeats, x, y } = moment;
    const fade =
      smoothstep(clamp01(beats / TORNADO.fadeIn)) *
      (1 - smoothstep(clamp01((beats - (totalBeats - TORNADO.fadeOut)) / TORNADO.fadeOut)));
    if (fade <= 0) {
      return;
    }
    view.glow.visible = true;
    view.glow.position.set(x, y);
    view.glow.scale.set((reach * TORNADO.glow) / textures.halo.radius);
    view.glow.alpha = 0.3 * fade * frame.light.haloAlpha;
    setTint(view.glow, AIR_COLOR);
    floor.circle(x, y, reach * 1.05).fill({ color: TORNADO_SILK.band, alpha: TORNADO.fill * fade });
    const spin = beats * TAU * (frame.calm ? TORNADO.calmSpin : TORNADO.spin);
    for (let index = 0; index < arms.length; index += 1) {
      const arm = arms[index];
      if (arm === undefined) {
        continue;
      }
      arm.begin();
      for (let step = 0; step <= TORNADO.steps; step += 1) {
        const share = step / TORNADO.steps;
        const r = (0.15 + 0.85 * share) * reach;
        const angle = share * TORNADO.turns + spin + (index * TAU) / TORNADO.arms;
        const width =
          reach * TORNADO.width * (0.35 + 0.65 * Math.sin(Math.PI * (0.15 + 0.85 * share)));
        arm.push(x + Math.cos(angle) * r, y + Math.sin(angle) * r, width);
      }
      arm.curl(1, reach * 0.17, 1, reach * 0.1);
      arm.draw(floor, 1, TORNADO_SILK, fade);
    }
  }

  function riftLine(loose: PropState, scale: number): void {
    const { reach, size } = moment;
    for (let along = 0, index = 0; along <= reach; along += RIFT.step * size, index += 1) {
      const across = (JAG[index % JAG.length] ?? 0) * scale;
      const px = loose.x + loose.vx * along - loose.vy * across;
      const py = loose.y + loose.vy * along + loose.vx * across;
      if (index === 0) {
        floor.moveTo(px, py);
      } else {
        floor.lineTo(px, py);
      }
    }
  }

  function drawRift(loose: PropState): void {
    const { reach, beats, totalBeats, size } = moment;
    const fade = 1 - clamp01((beats - (totalBeats - RIFT.fadeBeats)) / RIFT.fadeBeats);
    if (fade <= 0 || reach <= RIFT.step) {
      return;
    }
    const ax = loose.vx;
    const ay = loose.vy;
    const scale = RIFT.scale * size;
    for (const [width, color, alpha] of RIFT.lips) {
      riftLine(loose, scale);
      floor.stroke({
        width: width * scale,
        color,
        alpha: alpha * fade,
        cap: 'round',
        join: 'round',
      });
    }
    riftLine(loose, scale);
    floor.stroke({
      width: RIFT.crack * scale,
      color: 0x140a04,
      alpha: fade,
      cap: 'round',
      join: 'round',
    });
    for (const [width, color, alpha] of RIFT.core) {
      riftLine(loose, scale);
      floor.stroke({
        width: width * scale,
        color,
        alpha: alpha * fade,
        cap: 'round',
        join: 'round',
      });
    }
    for (
      let along = RIFT.step * size, index = 1;
      along <= reach;
      along += RIFT.step * size, index += 1
    ) {
      const rise = smoothstep(clamp01((reach - along) / RIFT.spikeRise));
      if (rise <= 0) {
        continue;
      }
      const side = index % 2 === 0 ? -1 : 1;
      const across = (JAG[index % JAG.length] ?? 0) * scale;
      const px = loose.x + ax * along - ay * across;
      const py = loose.y + ay * along + ax * across;
      const reachOut = RIFT.spike * scale * rise;
      const tipX = px - ay * side * reachOut + ax * RIFT.spikeLean * rise;
      const tipY = py + ax * side * reachOut + ay * RIFT.spikeLean * rise;
      const halfX = ax * RIFT.spikeHalf * scale;
      const halfY = ay * RIFT.spikeHalf * scale;
      floor
        .moveTo(px - halfX, py - halfY)
        .lineTo(tipX, tipY)
        .lineTo(px, py)
        .closePath()
        .fill({ color: 0xc99a5c, alpha: fade })
        .stroke({ width: 1, color: OUT, alpha: 0.9 * fade, join: 'round' })
        .moveTo(px, py)
        .lineTo(tipX, tipY)
        .lineTo(px + halfX, py + halfY)
        .closePath()
        .fill({ color: 0x6e4a26, alpha: fade })
        .stroke({ width: 1, color: OUT, alpha: 0.9 * fade, join: 'round' });
    }
  }

  function measure(state: SimState, loose: PropState, alpha: number, frame: Frame) {
    const owner = byId(state.players, loose.playerId);
    const effect = owner === undefined ? undefined : unleashes.get(owner.classId);
    if (owner === undefined || effect === undefined) {
      return undefined;
    }
    const { tempo } = frame;
    const element = loose.rank ?? FIRE;
    const total = unleashedTicks(effect, element, tempo);
    const age = total - loose.ticksLeft + alpha;
    moment.element = element;
    moment.size = statValue(owner, 'skillRadiusMul', 1);
    moment.reach = unleashedReach(effect, element, age, moment.size, tempo);
    moment.beats = age / tempo.ticksPerBeat;
    moment.totalBeats = total / tempo.ticksPerBeat;
    moment.share = age / total;
    moment.x = lerp(loose.prevX, loose.x, alpha);
    moment.y = lerp(loose.prevY, loose.y, alpha);
    return effect;
  }

  return {
    reset(): void {
      floor.clear();
      views?.releaseAll();
    },
    update(state: SimState, alpha: number, frame: Frame): void {
      if (unleashes.size === 0) {
        return;
      }
      floor.clear();
      const pool = viewPool();
      pool.begin();
      for (const loose of state.props ?? []) {
        const effect = loose.kind === UNLEASHED ? measure(state, loose, alpha, frame) : undefined;
        if (effect === undefined) {
          continue;
        }
        const view = pool.acquire(loose.id);
        view.glow.visible = false;
        for (const flame of view.flames) {
          flame.visible = false;
        }
        switch (moment.element) {
          case FIRE:
            drawRing(loose, effect, view, frame);
            break;
          case WATER:
            drawWave(loose, effect, frame);
            break;
          case AIR:
            drawTornado(view, frame);
            break;
          default:
            drawRift(loose);
        }
      }
      pool.end();
    },
    destroy(): void {
      floor.destroy();
      sheet.painted?.source.destroy();
    },
  };
}
